import qs from 'querystring'
import { setUser as setSentryUser } from '@sentry/nextjs'
import copy from 'copy-to-clipboard'
import type { User } from 'firebase/auth'
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth'
import cookie from 'js-cookie'
import jwtDecode from 'jwt-decode'
import omit from 'lodash-es/omit'
import Router from 'next/router'
import { eventChannel } from 'redux-saga'
import { cancel, delay, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  cancelled,
  call,
  fork,
  select,
  take,
  FixedTask
} from 'typed-redux-saga'
import SvgLink from '#/assets/images/icons/link.svg'
import Button from '#/components/Button'
import Snackbar from '#/components/Snackbar'
import { TH_TOKEN, TH_USER_ID } from '#/consts'
import { isAgent, isWFGEmail } from '#/consts/partners'
import { id2Plan, role2Name } from '#/consts/plan'
import { GUESTS_PAGE, PRICING_PAGE } from '#/consts/url'
import * as services from '#/services/user'
import { getAddresses } from '#/services/v4/geo/client'
import { UpdateStates as UpdateUI } from '#/store/actions/ui'
import * as Actions from '#/store/actions/user'
import { success, failure, successCreator, failureCreator, requestCreator } from '#/utils/action'
import { firebaseApp, getAuth } from '#/utils/firebase'
import { showSuccess } from '#/utils/message'
import notification from '#/utils/notification'
import { watchForDocument } from '#/utils/saga/firebase'
import showWelcomeMemberMessage from '#/utils/showWelcomeMesage'
import { authUrl } from '#/utils/url'
import { currentRole } from '../selectors/user'

let statsTimer: NodeJS.Timeout | undefined

function* watchForUserDoc(uid: string) {
  yield* watchForDocument(`users/${uid}`, function* (snapshot) {
    const user = snapshot.data() || { isAnonymous: true }
    yield put(Actions.UpdateStates({ info: user }))

    const userContext = {
      email: user.email || '',
      username: user.username || '',
      id: uid
    }
    setSentryUser(userContext)

    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        user_properties: {
          id: uid,
          job_role: user.role || '',
          email: user.email || '',
          displayName: user.displayName || '',
          phoneNumber: user.phoneNumber || ''
        }
      })
    }
  })
}

function* watchForAgentInfo(ownerId: string | undefined) {
  if (!ownerId) {
    yield put(
      Actions.UpdateStates({
        agentInfo: undefined,
        agentRole: undefined
      })
    )
  } else {
    yield* fork(() =>
      watchForDocument(`users/${ownerId}`, function* (snapshot) {
        yield put(Actions.UpdateStates({ agentInfo: snapshot.data() }))
      }))

    yield* fork(() =>
      watchForDocument(
        `users/${ownerId}/private/payment`,
        function* (snapshot) {
          const payment = snapshot.data() as TopHap.Payment
          yield put(Actions.UpdateStates({ agentRole: payment?.role }))
        }
      ))
  }
}

function* watchForUserPaymentDoc(user: User) {
  yield* watchForDocument(
    `users/${user.uid}/private/payment`,
    function* (snapshot) {
      let paymentInfo = snapshot.data() as TopHap.Payment

      if (user.isAnonymous === false) {
        if (!paymentInfo?.role) {
          paymentInfo = {
            ...paymentInfo,
            role: 'free'
          }
        }
      }

      yield* watchForAgentInfo(paymentInfo?.ownerId)

      yield put(Actions.UpdateStates({ paymentInfo }))
    }
  )
}

function* watchForUserPermissionsDoc(user: User) {
  yield* watchForDocument(
    `users/${user.uid}/private/permissions`,
    function* (snapshot) {
      const permissions = snapshot.data() as TopHap.UserPermissions
      yield put(Actions.UpdateStates({ permissions }))
    }
  )
}

function* watchForStats() {
  while (true) {
    yield put(Actions.GetUserStats())
    yield delay(1000 * 30)
  }
}

function* authStateChanged(user: User | null) {
  try {
    if (statsTimer) {
      clearInterval(statsTimer)
      statsTimer = undefined
    }
    cookie.remove(TH_TOKEN)

    // sign in with init token if exists
    const initToken = yield* select((_: TopHap.StoreState) => _.user.initToken)
    if (initToken) {
      const decoded = jwtDecode<{ user_id: string }>(initToken)
      if (user?.uid !== decoded.user_id) {
        yield put(Actions.SignInWithCustomToken({ token: initToken }))
        return
      }
    }

    if (user) {
      cookie.set(TH_USER_ID, user.uid)

      yield put(
        Actions.UpdateStates({
          isAnonymous: user.isAnonymous,
          uid: user.uid,
          providerId: user.providerData.length ?
            user.providerData[0]?.providerId :
            undefined
        } as Partial<TopHap.UserState>)
      )

      if (!user.isAnonymous) {
        yield put(Actions.GetCustomer())
        const token = yield* call(() => user.getIdToken())
        cookie.set(TH_TOKEN, token, { expires: 1 })
        yield* fork(watchForStats)
      } else {
        // sign in with init token
        const initToken = yield* select((_: TopHap.StoreState) => _.user.initToken)
        if (initToken) {
          yield put(Actions.SignInWithCustomToken({ token: initToken }))
        }
      }

      setSentryUser({ id: user.uid })

      // subscribe to user info doc
      yield* fork(() => watchForUserDoc(user.uid))

      // subscribe to payment info doc.
      yield* fork(() => watchForUserPaymentDoc(user))

      // subscribe to permissions info doc.
      yield* fork(() => watchForUserPermissionsDoc(user))

      yield put({ type: 'AUTH_STATE_CHANGED', user })
    } else {
      cookie.remove(TH_USER_ID)

      // sign in with init token if exists
      const initToken = yield* select((_: TopHap.StoreState) => _.user.initToken)
      if (initToken) {
        yield put(Actions.SignInWithCustomToken({ token: initToken }))
      } else {
        const auth = getAuth(firebaseApp)
        try {
          yield* call(signInAnonymously, auth)
        } catch (error) {
          console.error('Error while sign in anonymously', error)
        }
      }
    }
  } catch (err) {
    console.error('AuthStateChanged:', err)
  }
}

function* watchForFirebaseAuth() {
  let authChannel
  try {
    const auth = getAuth(firebaseApp)
    authChannel = eventChannel<{ user: User | null }>(emit =>
      onAuthStateChanged(auth, user => emit({ user })))

    let task: FixedTask<void> | undefined
    while (true) {
      const { user } = yield* take(authChannel)
      if (task) {
        yield cancel(task)
      }
      task = yield* fork(() => authStateChanged(user))
    }
  } catch (err) {
    console.error('Watching Auth State:', err)
  } finally {
    if (yield* cancelled()) {
      authChannel?.close()
    }
  }
}

function* _redirect(
  isSignIn = false,
  invitedBy = false,
  partner: TopHap.Partner | undefined = undefined
) {
  const router = Router
  const query = router.query

  if (!isSignIn) {
    if (!query.role) {
      showWelcomeMemberMessage(role2Name('free'), '', true, false, invitedBy, partner)
    }
  }

  if (query) {
    if (query.redirect) {
      const newQuery = omit(query, ['redirect', 'auth'])
      let queryString = qs.encode(newQuery)
      if (queryString) queryString = '?' + queryString

      let as
      if (query.redirect === 'pricing') {
        as = PRICING_PAGE
      } else {
        as = router.asPath.split('?')[0]
      }

      router.push(as + queryString)

      // CONSIDER: remove when it is unnecessary completely
      if (query.redirect === 'ctapopup') {
        yield put(UpdateUI({ ctaPopup: { visible: true } }))
      }
    }
  } else {
    const url = authUrl(router, 'signupsuccess', false)
    router.push(url.href)
  }
}

function* signUp(action: ReturnType<typeof Actions.SignUp>) {
  yield put(requestCreator(action.type))

  try {
    // if partner is wfg and emails is not wfg then throw error
    if (action.payload.partner === 'wfg' && !isWFGEmail(action.payload.email)) {
      throw 'Permission required for this partnership.'
    }

    const res = yield* call(services.signUp, action.payload)
    yield put(
      successCreator(action.type, {
        user: res,
        invitedBy: action.payload.invitedBy
      })
    )

    const { invitedBy, email, partner } = action.payload

    // create subscription for partners unless invited, if invited create customer in accept invite
    if (partner && !invitedBy) {
      yield put(Actions.CreateCustomer({ partner }))
      const createAction = yield* take([
        success(Actions.CreateCustomer.type),
        failure(Actions.CreateCustomer.type)])

      if (createAction.type === failure(Actions.CreateCustomer.type)) {
        throw 'Cannot Create Customer!'
      }
    }

    yield _redirect(false, !!invitedBy, partner)
    if (invitedBy) {
      yield put(
        Actions.AcceptInvitation({
          email,
          ownerId: invitedBy
        })
      )
    }
  } catch (err: any) {
    if (err.code === 'auth/email-already-in-use') {
      yield put(failureCreator(action.type, { err, showAlert: false }))
    } else {
      yield put(failureCreator(action.type, { err, showAlert: true }))
    }
  }
}

function* signIn(action: ReturnType<typeof Actions.SignIn>) {
  yield put(requestCreator(action.type))

  try {
    const { email, password } = action.payload
    const user = yield* call(services.signIn, email, password)
    if (!user) {
      throw { code: 'auth/user-not-found' }
    }
    yield put(successCreator(action.type, { user }))

    if (!user.emailVerified) {
      const confirm = yield* call(
        _resendConfirmMessage,
        'Please verify your account.',
        'If you cannot find our verification email, check your spam folder or resend it.'
      )
      if (confirm === 'Confirm') {
        services.sendActionEmail({
          action: 'verifyEmail',
          email: user.email,
          displayName: user.displayName
        })
      }
    }

    yield _redirect(true)
  } catch (err: any) {
    if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
      err.message = 'The username or password is incorrect.'
    }
    yield put(failureCreator(action.type, { err, showAlert: true }))
  }
}

function* signInWithGoogle(action: ReturnType<typeof Actions.SignInWithGoogle>) {
  yield put(requestCreator(action.type))

  try {
    const res = yield* call(services.signInWithGoogle, action.payload.token)
    yield put(successCreator(action.type, res))

    yield _redirect(!res.isFirstSignIn)
  } catch (err: any) {
    // signout google
    // @ts-ignore
    if (window.gapi) {
      // @ts-ignore
      const auth2 = window.gapi.auth2.getAuthInstance()
      if (auth2 != null) {
        auth2
          .signOut()
          .then(() => auth2.disconnect())
          .then()
      }
    }

    yield put(failureCreator(action.type, { err, showAlert: true }))
  }
}

function* signInWithFacebook(action: ReturnType<typeof Actions.SignInWithFacebook>) {
  yield put(requestCreator(action.type))

  try {
    const res = yield* call(services.signInWithFacebook, action.payload.token)
    yield put(successCreator(action.type, res))

    yield _redirect(!res.isFirstSignIn)
  } catch (err: any) {
    // signout facebook
    // @ts-ignore
    if (window.FB) {
      // @ts-ignore
      window.FB.logout()
    }

    yield put(failureCreator(action.type, { err, showAlert: true }))
  }
}

function* signInWithCustomToken(action: ReturnType<typeof Actions.SignInWithCustomToken>) {
  yield put(requestCreator(action.type))

  try {
    const { token } = yield* call(services.getCustomToken, action.payload.token)
    const res = yield* call(services.signInWithCustomToken, token)
    yield put(successCreator(action.type, res))

    // CONSIDER ME: disabled it for ios app
    // yield _redirect(!res.isFirstSignIn)
  } catch (err: any) {
    yield put(failureCreator(action.type, { err }))
  }
}

function* resendConfirm(action: ReturnType<typeof Actions.ResendConfirm>) {
  yield put(requestCreator(action.type))
  try {
    yield* call(services.sendActionEmail, {
      ...action.payload,
      action: 'verifyEmail' as const
    })
    yield put(successCreator(action.type, {}))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: true }))
  }
}

function _resendConfirmMessage(
  title: React.ReactNode,
  message: React.ReactNode
) {
  return new Promise((resolve: (value?: unknown) => void) => {
    const key = 'user_notconfiremd_notification'
    const resend = () => {
      notification.close(key)
      resolve('Confirm')
    }
    notification.show({
      autoClose: 7000,
      key,
      type: 'error',
      title,
      message,
      onClose: () => resolve('Cancel'),
      btn: <Button onClick={resend}>Resend</Button>
    })
  })
}

function* confirmUser(action: ReturnType<typeof Actions.ConfirmUser>) {
  yield put(requestCreator(action.type))

  try {
    yield* call(services.confirmUser, action.payload)
    yield put(successCreator(action.type, {}))
  } catch (err: any) {
    if (err.code === 'expired') {
      _resendConfirmMessage(
        'Verification link expired!',
        'Please resend it and try verifying again. If you cannot find the email, check the spam folder.'
      ).then(confirm => {
        if (confirm === 'Confirm') {
          services.sendActionEmail({
            displayName: err.details.displayName,
            email: err.details.email,
            action: 'verifyEmail'
          })
        }
      })
      yield put(failureCreator(action.type, { err, showAlert: false }))
    } else {
      yield put(
        failureCreator(action.type, {
          err: { message: 'User is already verified or verification link is expired.' },
          showAlert: true
        })
      )
    }
  }
}

function* signOut(action: ReturnType<typeof Actions.SignOut>) {
  yield put(requestCreator(action.type))

  try {
    yield* call(services.signOut)
    yield put(successCreator(action.type, {}))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err }))
  }
}

function* forgotPassword(action: ReturnType<typeof Actions.ForgotPassword>) {
  yield put(requestCreator(action.type))
  try {
    yield* call(services.sendActionEmail, {
      email: action.payload.email,
      action: 'resetPassword' as const
    })
    yield put(successCreator(action.type, {}))

    yield* call(showSuccess, {
      title: 'Password Reset',
      content: 'Reset link is sent to your email. Please check your email.'
    })
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: true }))
  }
}

function* resetPassword(action: ReturnType<typeof Actions.ResetPassword>) {
  yield put(requestCreator(action.type))
  try {
    yield* call(services.resetPassword, action.payload)

    yield put(successCreator(action.type, {}))

    yield* call(showSuccess, {
      title: 'Password Reset',
      content: 'New password is set successfully.'
    })
    const router = Router.router
    if (router) {
      const url = authUrl(router, 'signin')
      router.push(url.href)
    }
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: true }))
  }
}

function* updatePassword(action: ReturnType<typeof Actions.UpdatePassword>) {
  yield put(requestCreator(action.type))
  try {
    const { oldPassword, newPassword } = action.payload
    yield* call(services.updatePassword, oldPassword, newPassword)

    yield put(successCreator(action.type, {}))

    yield* call(showSuccess, {
      title: 'Password Update',
      content: 'New password is set successfully.'
    })
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: true }))
  }
}

function* updateProfile(action: ReturnType<typeof Actions.UpdateProfile>) {
  yield put(requestCreator(action.type))

  try {
    const updates: Partial<TopHap.UserInfo> = omit(action.payload, ['credential', 'photoUrl'])

    if (action.payload.photoUrl) {
      updates.photoUrl = yield* call(
        services.uploadImage,
        action.payload.photoUrl
      )
    }

    const user = yield* select((_: TopHap.StoreState) => _.user)

    if (action.payload.portal) {
      if (action.payload.portal.logoUrl) {
        updates.portal = {
          ...updates.portal,
          logoUrl: yield* call(
            services.uploadImage,
            action.payload.portal.logoUrl,
            'agent_logo'
          )
        }
      }

      updates.portal = {
        ...user.info.portal,
        ...updates.portal
      }
    }

    yield* call(services.updateProfile, updates, action.payload.credential)

    // updates stripe if needed
    if (updates.email || updates.displayName) {
      if (user.paymentInfo?.customer) {
        yield* call(services.updateCustomer, {
          email: updates.email,
          name: updates.displayName
        })
      }
    }

    yield put(successCreator(action.type, updates))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: true }))
  }
}

function* closeAccount(action: ReturnType<typeof Actions.CloseAccount>) {
  yield put(requestCreator(action.type))

  try {
    yield* call(services.closeAccount)
    yield put(successCreator(action.type, {}))
    yield put(Actions.SignOut())
  } catch (err: any) {
    yield put(failureCreator(action.type, { err }))
  }
}

function* getRecentSearches(action: ReturnType<typeof Actions.GetRecentSearches>) {
  yield put(requestCreator(action.type))

  try {
    const res: any = yield* call(services.getRecentSearches)

    yield put(
      successCreator(action.type, {
        recent: {
          searches: res.recent ?
            res.recent
              .map((e: string) => JSON.parse(e))
              .filter((e: TopHap.Place | null) => e) :
            [],
          views: res.views ?
            res.views
              .map((e: string) => JSON.parse(e))
              .filter((e: TopHap.Place | null) => e) :
            []
        }
      })
    )
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: false }))
  }
}

function* clearRecentSearches(action: ReturnType<typeof Actions.ClearRecentSearches>) {
  yield put(requestCreator(action.type))

  try {
    yield* call(services.clearRecentSearch)
    yield put(
      successCreator(action.type, { recent: { searches: [], views: [] } })
    )
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: false }))
  }
}

function* addRecentSearch(action: ReturnType<typeof Actions.AddRecentSearch>) {
  yield put(requestCreator(action.type))

  try {
    const recent = yield* select((_: TopHap.StoreState) => _.user.recent)
    const newSearches = [
      action.payload,
      ...recent.searches.filter(e => e.id !== action.payload.id)
    ]
      .filter(e => e)
      .slice(0, 5)

    yield* call(
      services.updateRecentSearch,
      newSearches.map(e => JSON.stringify(e))
    )
    yield put(successCreator(action.type, { searches: newSearches }))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: false }))
  }
}

function* addRecentView(action: ReturnType<typeof Actions.AddRecentView>) {
  yield put(requestCreator(action.type))

  try {
    const recent = yield* select((_: TopHap.StoreState) => _.user.recent)
    let address = recent.views.find(e => e.id === action.payload.id)
    if (!address) {
      address = (yield* call(getAddresses, action.payload.id)).items[0]
    }

    const newViews = [address, ...recent.views.filter(e => e.id !== action.payload.id)]
      .filter(e => e)
      .slice(0, 5)

    yield* call(
      services.updateRecentView,
      newViews.map(e => JSON.stringify(e))
    )
    yield successCreator(action.type, { views: newViews })
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: false }))
  }
}

function* createCustomer(action: ReturnType<typeof Actions.CreateCustomer>) {
  yield put(requestCreator(action.type))

  try {
    const fbp = cookie.get('_fbp')
    const fbc = cookie.get('_fbc')

    const res = yield* call(services.createCustomer, {
      ...action.payload,
      ...((fbp || fbc) && { tracking: { fbp, fbc } })
    })
    yield put(successCreator(action.type, { customer: res }))

    if (window.rewardful) {
      const email: string | undefined = yield select(
        (_: TopHap.StoreState) => _.user.info.email
      )
      window.rewardful('convert', { email })
    }
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: true }))
  }
}

function* updateCustomer(action: ReturnType<typeof Actions.UpdateCustomer>) {
  yield put(requestCreator(action.type))

  try {
    const res = yield* call(services.updateCustomer, action.payload)
    yield put(successCreator(action.type, { customer: res }))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: true }))
  }
}

function* getCustomer(action: ReturnType<typeof Actions.GetCustomer>) {
  yield put(requestCreator(action.type))

  try {
    const res = yield* call(services.getCustomer)
    yield put(successCreator(action.type, { customer: res }))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: false }))
  }
}

function* createSubscription(action: ReturnType<typeof Actions.CreateSubscription>) {
  yield put(requestCreator(action.type))

  try {
    const { planId, promotion } = action.payload
    const fbp = cookie.get('_fbp')
    const fbc = cookie.get('_fbc')

    const res = yield* call(
      services.createSubscription,
      planId,
      promotion,
      fbp,
      fbc
    )
    yield put(successCreator(action.type, { paymentInfo: res }))

    if (planId) {
      const plan = id2Plan(planId)
      if (plan) showWelcomeMemberMessage(plan.name, plan.period, false, true)
    }
  } catch (err: any) {
    yield put(failureCreator(action.type, { err }))
  }
}

function* updateSubscription(action: ReturnType<typeof Actions.UpdateSubscription>) {
  yield put(requestCreator(action.type))

  try {
    const { planId, promotion } = action.payload
    const res = yield* call(services.updateSubscription, planId, promotion)
    yield put(successCreator(action.type, { paymentInfo: res }))

    if (planId) {
      const plan = id2Plan(planId)
      if (plan) showWelcomeMemberMessage(plan.name, plan.period, false, false)
    }
  } catch (err: any) {
    yield put(failureCreator(action.type, { err }))
  }
}

function* deleteSubscription(action: ReturnType<typeof Actions.DeleteSubscription>) {
  yield put(requestCreator(action.type))

  try {
    const res = yield* call(services.deleteSubscription)
    yield put(successCreator(action.type, { paymentInfo: res }))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err }))
  }
}

function* addPromotion(action: ReturnType<typeof Actions.AddPromotion>) {
  yield put(requestCreator(action.type))

  try {
    const res = yield* call(services.addPromotion, action.payload.code)

    yield put(successCreator(action.type, { customer: res }))
  } catch (err: any) {
    yield put(
      failureCreator(action.type, {
        err:
          err.err === 'Invalid Promotion' ?
            {
              title: 'Wrong Code!',
              message: 'There is no such coupon.'
            } :
            err,
        showAlert: true
      })
    )
  }
}

function* deleteSource(action: ReturnType<typeof Actions.DeleteSource>) {
  yield put(requestCreator(action.type))

  try {
    const res = yield* call(services.deleteSource, action.payload.source)
    yield put(successCreator(action.type, { customer: res }))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: false }))
  }
}

function* getInvoices(action: ReturnType<typeof Actions.GetInvoices>) {
  yield put(requestCreator(action.type))

  try {
    const { after, limit } = action.payload
    const res = yield* call(services.getInvoices, after, limit)
    yield put(successCreator(action.type, res))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: false }))
  }
}

function* getUpcomingInvoice(action: ReturnType<typeof Actions.GetUpcomingInvoice>) {
  yield put(requestCreator(action.type))

  try {
    const res = yield* call(services.getUpcomingInvoice)
    yield put(successCreator(action.type, res))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: false }))
  }
}

function* sendResume(action: ReturnType<typeof Actions.SendResume>) {
  yield put(requestCreator(action.type))

  try {
    yield* call(services.sendResume, action.payload)

    yield put(successCreator(action.type, {}))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err }))
  }
}

function* sendFeedback(action: ReturnType<typeof Actions.SendFeedback>) {
  yield put(requestCreator(action.type))

  try {
    const history = yield* select((_: TopHap.StoreState) => _.user.history)
    yield* call(services.sendFeedback, {
      ...action.payload,
      payload: {
        ...action.payload.payload,
        history,
        resolution: `${window.innerWidth} * ${window.innerHeight}`,
        userAgent: navigator.userAgent
      }
    })

    yield put(successCreator(action.type, {}))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err }))
  }
}

function* requestInfo(action: ReturnType<typeof Actions.RequestInfo>) {
  yield put(requestCreator(action.type))

  try {
    const history = yield* select((_: TopHap.StoreState) => _.user.history)
    yield* call(services.requestInfo, {
      ...action.payload,
      payload: {
        ...action.payload.payload,
        history,
        resolution: `${window.innerWidth} * ${window.innerHeight}`,
        userAgent: navigator.userAgent
      }
    })

    yield put(successCreator(action.type, {}))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err }))
  }
}

function copyLink(link: string) {
  copy(link)
  Snackbar.show({ message: 'Link copied to clipboard.' })
}

function* shareLink(action: ReturnType<typeof Actions.ShareLink>) {
  yield put(requestCreator(action.type))

  try {
    const { type, entityId, url, copy = true, callback } = action.payload
    const state: TopHap.StoreState = yield* select()
    const userRole = currentRole(state)
    const branded = state.user.info?.job ? isAgent(state.user.info.job) : false

    let link = url
    if (userRole === 'expert' || userRole === 'pro') {
      link = url + '?' + qs.encode({ u: state.user.uid, s: entityId, br: branded })
    }

    if (copy) {
      if (navigator.share) {
        navigator.share({
          title: 'TopHap',
          url: link
        })
          .then()
          .catch(err => {
            if (err.name !== 'AbortError') {
              copyLink(link)
            }
          })
      } else {
        copyLink(link)
      }
    }

    if (callback) callback(link)

    yield* call(services.shareLink, {
      entityId,
      type,
      url
    })
    yield put(successCreator(action.type, {}))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: false }))
  }
}

function* getShareLink(action: ReturnType<typeof Actions.GetShareLink>) {
  yield put(requestCreator(action.type))

  try {
    const res = yield* call(services.getShareLink, action.payload)
    yield put(successCreator(action.type, {
      sharedRole: res.role,
      linkBrandedAgentInfo: res.agentInfo
    }))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: false }))
  }
}

function* inviteGuest(action: ReturnType<typeof Actions.InviteGuest>) {
  yield put(requestCreator(action.type))

  try {
    const res = yield* call(services.inviteGuest, action.payload)
    yield put(successCreator(action.type, res))

    const content = action.payload.ownerId ? (
      'Your friend has been invited.'
    ) : (
      <div style={{ maxWidth: 300 }}>
        Review and manage your guests in the
        {' '}
        <a href={GUESTS_PAGE} rel='noreferrer' target='_blank'>
          Guests
        </a>
        {' '}
        section of your Account page.
      </div>
    )
    const actions = (
      <Button
        className='ml-8 mr-auto rounded px-3 text-sm text-primary'
        onClick={() => copyLink(res.link)}
      >
        <SvgLink className='mr-2' />
        Copy invite link
      </Button>
    )

    yield* call(showSuccess, {
      title: 'Invitation sent!',
      content,
      actions,
      okText: 'Continue'
    })
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: true, errMsgPath: 'data' }))
  }
}

function* acceptInvitation(action: ReturnType<typeof Actions.AcceptInvitation>) {
  yield put(requestCreator(action.type))

  try {
    yield* call(services.acceptInvitation, action.payload)
    yield put(successCreator(action.type, {}))
  } catch (err: any) {
    if (err.err === 'No Invitation') {
      yield put(
        failureCreator(action.type, {
          err: {
            title: 'Invitation no longer valid!',
            message:
              'Please ask your agent for an invite, or create an account without an agent.',
            autoClose: 6000
          },
          showAlert: true
        })
      )
    } else {
      yield put(failureCreator(action.type, { err }))
    }
  }
}

function* removeInvitation(action: ReturnType<typeof Actions.RemoveInvitation>) {
  yield put(requestCreator(action.type))

  try {
    yield* call(services.removeInvitation, action.payload)
    yield put(successCreator(action.type, {}))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err }))
  }
}

function* getUserStats(action: ReturnType<typeof Actions.GetUserStats>) {
  yield put(requestCreator(action.type))

  try {
    // const uid = yield* select((_: TopHap.StoreState) => _.user.uid)
    // const res = uid ? yield* call(services.getUserStats, [uid]) : []

    // yield put(successCreator(action.type, { stats: res[0] }))
    yield put(successCreator(action.type, { stats: undefined }))
  } catch (err: any) {
    yield put(failureCreator(action.type, { err, showAlert: false }))
  }
}

export default function* userSaga() {
  yield* fork(watchForFirebaseAuth)
  yield takeLatest(Actions.SignUp, signUp)
  yield takeLatest(Actions.SignIn, signIn)
  yield takeLatest(Actions.SignInWithGoogle, signInWithGoogle)
  yield takeLatest(Actions.SignInWithFacebook, signInWithFacebook)
  yield takeLatest(Actions.SignInWithCustomToken, signInWithCustomToken)
  yield takeLatest(Actions.ResendConfirm, resendConfirm)
  yield takeLatest(Actions.ConfirmUser, confirmUser)
  yield takeLatest(Actions.SignOut, signOut)
  yield takeLatest(Actions.ForgotPassword, forgotPassword)
  yield takeLatest(Actions.ResetPassword, resetPassword)
  yield takeLatest(Actions.UpdatePassword, updatePassword)
  yield takeLatest(Actions.UpdateProfile, updateProfile)
  yield takeLatest(Actions.CloseAccount, closeAccount)
  yield takeLatest(Actions.GetRecentSearches, getRecentSearches)
  yield takeLatest(Actions.ClearRecentSearches, clearRecentSearches)
  yield takeLatest(Actions.AddRecentSearch, addRecentSearch)
  yield takeLatest(Actions.AddRecentView, addRecentView)
  yield takeLatest(Actions.CreateCustomer, createCustomer)
  yield takeLatest(Actions.UpdateCustomer, updateCustomer)
  yield takeLatest(Actions.GetCustomer, getCustomer)
  yield takeLatest(Actions.CreateSubscription, createSubscription)
  yield takeLatest(Actions.UpdateSubscription, updateSubscription)
  yield takeLatest(Actions.DeleteSubscription, deleteSubscription)
  yield takeLatest(Actions.AddPromotion, addPromotion)
  yield takeLatest(Actions.DeleteSource, deleteSource)
  yield takeLatest(Actions.GetInvoices, getInvoices)
  yield takeLatest(Actions.GetUpcomingInvoice, getUpcomingInvoice)
  yield takeLatest(Actions.SendFeedback, sendFeedback)
  yield takeLatest(Actions.SendResume, sendResume)
  yield takeLatest(Actions.RequestInfo, requestInfo)
  yield takeEvery(Actions.ShareLink, shareLink)
  yield takeLatest(Actions.GetShareLink, getShareLink)
  yield takeLatest(Actions.InviteGuest, inviteGuest)
  yield takeEvery(Actions.AcceptInvitation, acceptInvitation)
  yield takeEvery(Actions.RemoveInvitation, removeInvitation)
  yield takeLatest(Actions.GetUserStats, getUserStats)
}
